import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { AlertCircle } from 'lucide-react';

/**
 * Public hand-off route used by the SuperAdmin app to log into bizcore-web as a
 * tenant user. The SuperAdmin opens:
 *   https://<this-app>/impersonate#token=<URL-encoded JWT>
 * where the JWT is a normal tenant access token (claims: uid, tid, tslug, role,
 * email). There is no refresh token and no user object in the hand-off.
 *
 * On mount we read the token from the URL hash, populate the auth store the same
 * way a normal login does, run bootstrap() to load permissions/modules, scrub
 * the token from the address bar, then redirect to the dashboard.
 */
export const Impersonate = () => {
  const navigate = useNavigate();
  const impersonate = useAuthStore((state) => state.impersonate);
  const bootstrap = useAuthStore((state) => state.bootstrap);
  const logout = useAuthStore((state) => state.logout);
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects in dev; guard so we only run once.
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      // Hash format: #token=<URL-encoded JWT>
      const hash = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
      const params = new URLSearchParams(hash);
      const raw = params.get('token');
      const token = raw ? decodeURIComponent(raw) : '';

      // Scrub the token from the URL so it isn't left in the address bar/history.
      window.history.replaceState(null, '', window.location.pathname);

      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      const ok = impersonate(token);
      if (!ok) {
        setError('This impersonation link is invalid or expired.');
        logout();
        return;
      }

      // Populate permissions/modules/business from the backend, mirroring login.
      // If /bootstrap is unreachable the store falls back to the token role +
      // core modules (it never fails open), so we continue regardless.
      try {
        await bootstrap();
      } catch {
        // bootstrap() already degrades gracefully; ignore.
      }

      navigate('/dashboard', { replace: true });
    };

    run();
  }, [impersonate, bootstrap, logout, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-[440px] rounded-2xl border border-border bg-card p-8 text-center shadow-sm" data-testid="impersonate-error">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger-bg text-danger">
            <AlertCircle size={24} />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Impersonation failed</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            data-testid="impersonate-login"
            className="mt-6 inline-flex h-11 items-center justify-center rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground motion-safe:transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" />
        <p className="text-sm font-medium text-muted-foreground">Signing you in…</p>
      </div>
    </div>
  );
};
